import { useCallback, useEffect } from 'react'
import { UseFormHandleSubmit, UseFormReset } from 'react-hook-form'
import { toast } from 'react-toastify'
import { toastSyncSuccess } from 'src/utils/syncToast'
import { useAuth } from 'src/contexts/auth'
import { IAnimal } from 'src/dto/animal.dto'
import { GenotypingDto, CreateGenotypingDto, UpdateGenotypingDto } from 'src/dto/genotyping.dto'
import { useGetAnimalList } from 'src/hooks/api/animal'
import { useCreateGenotyping, useUpdateGenotyping } from 'src/hooks/api/genotyping'

interface UseRegisterProps {
  data: GenotypingDto | null
  handleSubmit: UseFormHandleSubmit<UpdateGenotypingDto>
  reset: UseFormReset<UpdateGenotypingDto>
  onClose: () => void
}

export const useRegister = ({ handleSubmit, onClose, data, reset }: UseRegisterProps) => {
  const { userFarm: farmId } = useAuth()

  const { data: animalListData } = useGetAnimalList()
  const animalListRaw = (animalListData?.pages.flatMap((page) => page.list) ?? []).filter(
    (animal) => animal.categoria !== 'VD' && animal.categoria !== 'MT'
  )

  const { mutateAsync: createGenotyping, isPending: isLoadingCreate } = useCreateGenotyping()
  const { mutateAsync: updateGenotyping, isPending: isLoadingUpdate } = useUpdateGenotyping()

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
    }
  }, [])

  const onSubmit = handleSubmit(async (formData: UpdateGenotypingDto) => {
    try {
      const animal = animalListRaw.find((animal) => animal.idAnimal === formData.idAnimal) as IAnimal

      if (data?.idGenotipagem) {
        const updateData: UpdateGenotypingDto = {
          ...data,
          idAnimal: formData.idAnimal,
          idFazenda: farmId!,
          codigoBarras: formData.codigoBarras,
          animal
        }
        await updateGenotyping(updateData)
        toastSyncSuccess('Genotipagem atualizada com sucesso!')
      } else {
        const createData: CreateGenotypingDto = {
          idAnimal: formData.idAnimal,
          idFazenda: farmId!,
          codigoBarras: formData.codigoBarras,
          animal,
          idDeps: ''
        }
        await createGenotyping(createData)
        toastSyncSuccess('Genotipagem criada com sucesso!')
      }
      onClose()
    } catch (err: any) {
      console.error(err)
    }
  })

  useEffect(() => {
    if (data) {
      reset({
        idAnimal: data.idAnimal || '',
        idFazenda: data.idFazenda || '',
        codigoBarras: data.codigoBarras || ''
      })
    }
  }, [data, reset])

  return {
    isLoading: isLoadingCreate || isLoadingUpdate,
    onSubmit,
    handleKeyDown
  }
}
